import { useEffect } from "react"
import { isEmpty } from "lodash"
import { Box, CircularProgress, Container, Grid, Link, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material"
import moment from "moment"
import { useGetDailyCheckinDetailMutation } from "store/dailycheckin"
import Dialog from "components/dialog/dialog"
import DialogStyle from "components/dialog/style"

export default function Detail(props: {
  id: number
  open: boolean
  onClose: () => void
}) {
  const [getDailyCheckinDetail, { data, isLoading }] = useGetDailyCheckinDetailMutation()
  const { id, open, onClose } = props

  const formatDate = (e: string) => (!isEmpty(e) ? moment(e).format('DD-MM-YYYY HH:mm') : '-');

  useEffect(() => {
    if (id && open) {
      getDailyCheckinDetail(id)
    }
  }, [id, open])

  if (!open) return null

  return (
    <Dialog
      title='View Daily Check-In'
      open={open}
      onCancel={onClose}
      onSubmit={undefined}
      maxWidth='md'
      loading={false}
    >
      <Container {...DialogStyle.Container} sx={{ ...DialogStyle.Container.sx, mt: 2, mb: 2 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color='inherit' />
          </Box>
        )}
        {!isLoading && data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Daily Checkin Name" value={data.title || '-'} fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            <TextField label="Description" value={data.descriptionEn || data.descriptionId || '-'} fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Valid From" value={formatDate(data.periodStart)} fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Valid Until" value={formatDate(data.periodEnd)} fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>

            {data.bannerImage && (
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>Banner Image</Typography>
                 <TextField 
                    label="Banner" 
                    value={data.bannerImage ? data.bannerImage.split('/').pop() || '-' : '-'} 
                    fullWidth 
                    InputProps={{ readOnly: true }} 
                    InputLabelProps={{ shrink: true }} 
                    />
                <Box display='flex' justifyContent='center' alignItems='center' sx={{ border: '1px solid #ccc', p: 1, borderRadius: 1 }}>
                  <img src={data.bannerImage} alt='Banner' style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} />
                </Box>
              </Box>
            )}

            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Consecutive Days</Typography>
            <Table size="small" sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>

             <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Check In</TableCell>
                  <TableCell>Reward Type</TableCell>
                  <TableCell>Voucher</TableCell>
                  <TableCell>Quota</TableCell>
                  <TableCell>Stock Resource</TableCell>
                  <TableCell>Stock Number</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rewards?.map((reward, index) => (
                  <TableRow key={index}>
                    <TableCell>{reward.day}</TableCell>
                    <TableCell>{reward.rewardType}</TableCell>
                    <TableCell>
                      {reward.rewardType === 'Voucher' 
                        ? `${reward.voucherName  || '-'}` 
                        : `${reward.coinValue} Coins`}
                    </TableCell>
                    <TableCell>{reward.rewardType === 'Voucher' ? (reward.quotaValue ?? '-') : '-'}</TableCell>
                    <TableCell>{reward.stockResource || '-'}</TableCell>
                    <TableCell>{reward.stockNumber || '-'}</TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </Table>
             {/* Audit Trail */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Audit Trail</Typography>
                {data.auditTrails?.length > 0 ? (
              <Box sx={{ mb: 2 }}>
                {data.auditTrails.map((audit, index) => (
                  <Box key={index} sx={{ mb: 1, pb: 1, borderBottom: index < data.auditTrails.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                      <Link component="span" color="primary" sx={{ cursor: 'pointer' }}>
                        {audit.username}
                      </Link>
                    </Typography>
                    <Typography component="span" sx={{ ml: 0.5 }}>
                      made changes • {formatDate(audit.changedAt)}
                    </Typography>
                    {audit.auditTrailDetails?.length > 0 && (
                      <Table size="small" sx={{ mt: 1, ml: 2, border: '1px solid #e0e0e0', width: 'auto', minWidth: 500 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Reward Day Voucher</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Original</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>New</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {audit.auditTrailDetails.map((detail, dIndex) => (
                            <TableRow key={dIndex}>
                              <TableCell>{detail.day}</TableCell>
                              <TableCell>{detail.originalVoucher || '-'}</TableCell>
                              <TableCell>{detail.newVoucher || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>No audit trail available</Typography>
            )}
  {/* Created At & Disabled At */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField 
                  label="Created At" 
                  value={formatDate(data.createdAt)} 
                  fullWidth 
                  InputProps={{ readOnly: true }} 
                  InputLabelProps={{ shrink: true }} 
                />
              </Grid>
              <Grid item xs={6}>
                <TextField 
                  label="Disabled At" 
                  value={data.disabledAt ? formatDate(data.disabledAt) : '-'} 
                  fullWidth 
                  InputProps={{ readOnly: true }} 
                  InputLabelProps={{ shrink: true }} 
                />
              </Grid>
            </Grid>


            
          </Box>
        )}
      </Container>
    </Dialog>
  )
}
